import { FC } from 'react';

import {
  ActivitySvg,
  AiSvg,
  AlertsSvg,
  AnalyticsSvg,
  ErrorSvg,
  EventsSvg,
  GallerySvg,
  GenericSvg,
  GroupSvg,
  NotFoundSvg,
  OffersSvg,
  OrdersSvg,
  OrganizationsSvg,
  PersonSvg,
  PoiSvg,
  QrSvg,
  RentalsSvg,
  ReportsSvg,
  SearchSvg,
  SetupSvg,
  ShopSvg,
  SpaSvg,
  SurveysSvg,
  UploadSvg,
} from './TableEmptyState.styled';

/**
 * Animowane ilustracje pustych stanów w stylu line-art (stonowane, jak placeholdery
 * w nowoczesnych panelach). Każda ilustracja jest samodzielnym SVG — kolor linii
 * dziedziczony przez `currentColor`, a delikatny akcent marki (teal) podkreśla
 * kluczowy element. Animacje sterowane są klasami z `TableEmptyState.styled.ts`.
 */

// Akcent marki HotelSpot (primary teal) — używany oszczędnie na jednym detalu.
const ACCENT = '#0a7465';

// Wspólne atrybuty line-artu dla wszystkich ilustracji.
const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

// Miękkie obłoki tła — wspólne dla wszystkich motywów.
const Blobs: FC = () => (
  <g opacity={0.06}>
    <ellipse className="blob-1" cx={82} cy={72} rx={48} ry={30} fill="currentColor" />
    <ellipse className="blob-2" cx={142} cy={84} rx={42} ry={26} fill="currentColor" />
  </g>
);

// Ozdobne migoczące znaczki (+ / x / romb) — życie w narożnikach.
const Sparks: FC = () => (
  <g {...stroke}>
    <path className="spark spark-1" d="M178 50 v10 M173 55 h10" />
    <path className="spark spark-2" d="M41 62 l6 6 M47 62 l-6 6" />
    <path
      className="spark spark-3"
      d="M165 32 l4 4 -4 4 -4 -4 z"
      fill={ACCENT}
      stroke={ACCENT}
      opacity={0.7}
    />
  </g>
);

// Zamówienia / zgłoszenia — dzwonek recepcyjny (concierge bell), który „dzwoni",
// z falami dźwięku rozchodzącymi się na boki.
export const OrdersIllustration: FC = () => (
  <OrdersSvg viewBox="0 0 220 150" role="img" aria-hidden="true">
    <Blobs />

    {/* miękki cień pod dzwonkiem */}
    <ellipse cx={110} cy={123} rx={48} ry={6} fill="currentColor" opacity={0.08} />

    {/* fale dźwięku */}
    <g {...stroke} opacity={0.55}>
      <g className="wave">
        <path d="M76 84 q-9 5 -9 15" />
        <path d="M71 76 q-15 7 -14 24" />
      </g>
      <g className="wave wave-r">
        <path d="M144 84 q9 5 9 15" />
        <path d="M149 76 q15 7 14 24" />
      </g>
    </g>

    {/* dzwonek */}
    <g className="bell" {...stroke}>
      {/* kopuła */}
      <path d="M80 113 A30 39 0 0 1 140 113 Z" fill="currentColor" fillOpacity={0.05} />
      {/* delikatny refleks na kopule */}
      <path d="M95 105 a17 22 0 0 1 12 -27" opacity={0.4} />
      {/* trzonek i gałka */}
      <path d="M110 75 v-9" />
      <circle cx={110} cy={62} r={5} fill={ACCENT} stroke={ACCENT} opacity={0.75} />
      {/* nóżki */}
      <path d="M86 118 l-4 5 M134 118 l4 5" />
    </g>

    {/* taca / podstawa */}
    <path {...stroke} d="M70 116 h80" />

    <Sparks />
  </OrdersSvg>
);

// Rejestracje na wydarzenia — kalendarz z wyróżnionym, pulsującym dniem.
export const EventsIllustration: FC = () => (
  <EventsSvg viewBox="0 0 220 150" role="img" aria-hidden="true">
    <Blobs />
    <ellipse cx={110} cy={128} rx={44} ry={5} fill="currentColor" opacity={0.08} />

    <g className="floaty">
      {/* spinacze / kółka u góry */}
      <path {...stroke} d="M90 50 v12 M130 50 v12" />

      {/* korpus kalendarza */}
      <g {...stroke}>
        <rect x={72} y={58} width={76} height={66} rx={9} fill="currentColor" fillOpacity={0.04} />
        {/* pasek nagłówka */}
        <path
          d="M72 67 a9 9 0 0 1 9 -9 h58 a9 9 0 0 1 9 9 v8 h-76 z"
          fill="currentColor"
          fillOpacity={0.09}
          stroke="none"
        />
        <path d="M72 75 h76" />
      </g>

      {/* dni — siatka kropek */}
      <g fill="currentColor" opacity={0.4}>
        <circle cx={88} cy={90} r={2.4} />
        <circle cx={132} cy={90} r={2.4} />
        <circle cx={88} cy={107} r={2.4} />
        <circle cx={110} cy={107} r={2.4} />
        <circle cx={132} cy={107} r={2.4} />
      </g>

      {/* wyróżniony dzień (nowy zapis) */}
      <circle className="day" cx={110} cy={90} r={7} fill={ACCENT} opacity={0.85} />
    </g>

    <Sparks />
  </EventsSvg>
);

// Odpowiedzi z ankiet — dymek rozmowy z animowanymi kropkami „pisania".
export const SurveysIllustration: FC = () => (
  <SurveysSvg viewBox="0 0 220 150" role="img" aria-hidden="true">
    <Blobs />
    <ellipse cx={108} cy={126} rx={46} ry={5} fill="currentColor" opacity={0.08} />

    <g className="floaty">
      {/* mniejszy dymek w tle */}
      <g {...stroke} opacity={0.45}>
        <rect x={128} y={52} width={48} height={30} rx={9} fill="currentColor" fillOpacity={0.04} />
        <path d="M140 82 v9 l11 -9" fill="currentColor" fillOpacity={0.04} />
      </g>

      {/* główny dymek */}
      <g {...stroke}>
        <rect x={52} y={62} width={92} height={52} rx={13} fill="currentColor" fillOpacity={0.05} />
        <path d="M72 114 v15 l17 -15" fill="currentColor" fillOpacity={0.05} />
      </g>

      {/* kropki „pisania" */}
      <g fill="currentColor">
        <circle className="dot dot-1" cx={82} cy={88} r={4.5} />
        <circle className="dot dot-2" cx={98} cy={88} r={4.5} fill={ACCENT} opacity={0.85} />
        <circle className="dot dot-3" cx={114} cy={88} r={4.5} />
      </g>
    </g>

    <Sparks />
  </SurveysSvg>
);

// Interakcje / analityka — wykres słupkowy z „oddychającymi" słupkami, stałym
// słupkiem rekordowym (pulsujący punkt szczytu) i unoszącą się strzałką trendu.
export const AnalyticsIllustration: FC = () => (
  <AnalyticsSvg viewBox="0 0 220 150" role="img" aria-hidden="true">
    <Blobs />
    <ellipse cx={112} cy={120} rx={62} ry={6} fill="currentColor" opacity={0.08} />

    {/* osie wykresu */}
    <path {...stroke} d="M58 42 V112 H168" opacity={0.45} />
    {/* linia bazowa (przerywana) */}
    <path
      d="M58 112 H168"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeDasharray="3 4"
      opacity={0.25}
    />

    {/* słupki danych (oddychające) */}
    <g fill="currentColor" opacity={0.5}>
      <rect className="bar bar-1" x={68} y={82} width={13} height={30} rx={3.5} />
      <rect className="bar bar-2" x={89} y={66} width={13} height={46} rx={3.5} />
      <rect className="bar bar-3" x={110} y={88} width={13} height={24} rx={3.5} />
      <rect className="bar bar-4" x={152} y={72} width={13} height={40} rx={3.5} />
    </g>

    {/* słupek rekordowy (stały) z pulsującym punktem szczytu */}
    <rect x={131} y={54} width={13} height={58} rx={3.5} fill={ACCENT} opacity={0.85} />
    <circle className="peak" cx={137.5} cy={54} r={4.5} fill={ACCENT} />

    {/* strzałka trendu w górę (unosząca się) */}
    <g className="trend" {...stroke}>
      <path d="M130 50 L150 34" />
      <path d="M142 34 H150 V42" />
    </g>

    <Sparks />
  </AnalyticsSvg>
);

// Dziennik aktywności — linia pulsu z „żywym" kursorem (ping) i wierszami kanału.
export const ActivityIllustration: FC = () => (
  <ActivitySvg viewBox="0 0 220 150" role="img" aria-hidden="true">
    <Blobs />
    <ellipse cx={110} cy={124} rx={56} ry={6} fill="currentColor" opacity={0.08} />

    {/* linia pulsu (aktywność) */}
    <path {...stroke} d="M40 80 H80 L90 52 L100 100 L108 80 H176" opacity={0.85} />
    {/* „żywy" kursor — ping + punkt na końcu linii */}
    <circle className="ping" cx={176} cy={80} r={9} fill="none" stroke={ACCENT} strokeWidth={2} />
    <circle className="peak" cx={176} cy={80} r={4.5} fill={ACCENT} />

    {/* wiersze kanału aktywności */}
    <g className="feed">
      <circle cx={54} cy={108} r={3.5} fill="currentColor" opacity={0.35} />
      <path d="M66 108 H150" stroke="currentColor" strokeWidth={3} strokeLinecap="round" opacity={0.3} />
      <circle cx={54} cy={120} r={3.5} fill="currentColor" opacity={0.25} />
      <path d="M66 120 H128" stroke="currentColor" strokeWidth={3} strokeLinecap="round" opacity={0.22} />
    </g>

    <Sparks />
  </ActivitySvg>
);

// Sklep (zamówienia produktów) — papierowa torba z unoszącą się metką ceny.
export const ShopIllustration: FC = () => (
  <ShopSvg viewBox="0 0 220 150" role="img" aria-hidden="true">
    <Blobs />
    <ellipse cx={108} cy={126} rx={46} ry={6} fill="currentColor" opacity={0.08} />

    <g className="floaty">
      {/* korpus torby */}
      <path
        {...stroke}
        d="M82 76 h52 a4 4 0 0 1 4 4.4 l-5 38 a8 8 0 0 1 -8 7.2 H91 a8 8 0 0 1 -8 -7.2 l-5 -38 a4 4 0 0 1 4 -4.4 Z"
        fill="currentColor"
        fillOpacity={0.05}
      />
      {/* zagięcie góry torby */}
      <path {...stroke} d="M79 82 q29 -8 58 0" opacity={0.55} />
      {/* uchwyt (akcent) */}
      <path
        d="M97 78 q11 -20 22 0"
        fill="none"
        stroke={ACCENT}
        strokeWidth={2.5}
        strokeLinecap="round"
        opacity={0.85}
      />
    </g>

    {/* unosząca się metka ceny (akcent) */}
    <g className="tag">
      <path
        {...stroke}
        d="M140 56 H156 V74 H140 L130 65 Z"
        fill={ACCENT}
        fillOpacity={0.14}
        stroke={ACCENT}
      />
      <circle cx={150} cy={62} r={2.4} fill={ACCENT} stroke="none" />
    </g>

    <Sparks />
  </ShopSvg>
);

// Rezerwacje wypożyczalni — zegar z obracającą się wskazówką + badge kalendarza.
export const RentalsIllustration: FC = () => (
  <RentalsSvg viewBox="0 0 220 150" role="img" aria-hidden="true">
    <Blobs />
    <ellipse cx={104} cy={124} rx={48} ry={6} fill="currentColor" opacity={0.08} />

    {/* tarcza zegara */}
    <g {...stroke}>
      <circle cx={100} cy={80} r={30} fill="currentColor" fillOpacity={0.04} />
      {/* znaczniki 12 / 3 / 6 / 9 */}
      <path d="M100 56 v5 M124 80 h-5 M100 104 v-5 M76 80 h5" opacity={0.6} />
      <circle cx={100} cy={80} r={2.6} fill="currentColor" stroke="none" />
      {/* wskazówka godzinowa (stała) */}
      <path d="M100 80 L114 74" opacity={0.7} />
    </g>

    {/* wskazówka minutowa (obraca się) — niewidoczny kwadrat centruje obrót na tarczy */}
    <g className="hand">
      <rect x={70} y={50} width={60} height={60} fill="none" stroke="none" />
      <path {...stroke} d="M100 80 L100 58" stroke={ACCENT} />
    </g>

    {/* badge kalendarza (data rezerwacji) */}
    <g className="tag" {...stroke}>
      <rect x={128} y={96} width={30} height={26} rx={4} fill="currentColor" fillOpacity={0.05} />
      <path d="M128 104 H158" />
      <path d="M136 92 v6 M150 92 v6" />
      <circle cx={143} cy={113} r={3} fill={ACCENT} stroke="none" />
    </g>

    <Sparks />
  </RentalsSvg>
);

// Brak wyników wyszukiwania / filtrowania — lupa „skanująca" z pustą soczewką.
export const SearchIllustration: FC = () => (
  <SearchSvg viewBox="0 0 220 150" role="img" aria-hidden="true">
    <Blobs />
    <ellipse cx={108} cy={124} rx={44} ry={6} fill="currentColor" opacity={0.08} />

    <g className="floaty">
      <g className="scan" {...stroke}>
        {/* soczewka */}
        <circle cx={98} cy={74} r={26} fill="currentColor" fillOpacity={0.04} />
        {/* refleks */}
        <path d="M86 64 a14 14 0 0 1 9 -6" opacity={0.5} />
        {/* uchwyt */}
        <path d="M117 93 L136 112" strokeWidth={5} strokeLinecap="round" />
        {/* „brak wyników" w soczewce */}
        <path d="M88 74 H108" strokeDasharray="3 4" opacity={0.5} />
      </g>
    </g>

    <Sparks />
  </SearchSvg>
);

// Oferty specjalne / promocje — prezent z kokardą (akcent wstążki) unoszący się.
export const OffersIllustration: FC = () => (
  <OffersSvg viewBox="0 0 220 150" role="img" aria-hidden="true">
    <Blobs />
    <ellipse cx={110} cy={126} rx={44} ry={6} fill="currentColor" opacity={0.08} />

    <g className="floaty">
      {/* korpus pudełka */}
      <rect {...stroke} x={80} y={88} width={60} height={34} rx={3} fill="currentColor" fillOpacity={0.05} />
      {/* wieczko */}
      <rect {...stroke} x={74} y={76} width={72} height={16} rx={3} fill="currentColor" fillOpacity={0.07} />
      {/* pionowa wstążka (akcent) */}
      <rect x={104} y={76} width={12} height={46} fill={ACCENT} opacity={0.18} />
      <path {...stroke} d="M110 76 V122" stroke={ACCENT} opacity={0.55} />

      {/* kokarda (pulsuje) */}
      <g className="pop">
        <path
          {...stroke}
          d="M110 76 C98 60 82 66 96 78 M110 76 C122 60 138 66 124 78"
          stroke={ACCENT}
          fill={ACCENT}
          fillOpacity={0.12}
        />
        <circle cx={110} cy={76} r={4} fill={ACCENT} stroke="none" />
      </g>
    </g>

    <Sparks />
  </OffersSvg>
);

// Alerty / ogłoszenia — megafon z falami dźwięku rozchodzącymi się w bok.
export const AlertsIllustration: FC = () => (
  <AlertsSvg viewBox="0 0 220 150" role="img" aria-hidden="true">
    <Blobs />
    <ellipse cx={100} cy={122} rx={46} ry={6} fill="currentColor" opacity={0.08} />

    <g className="floaty">
      {/* korpus megafonu (stożek) */}
      <path
        {...stroke}
        d="M62 70 L90 56 L90 100 L62 86 Z"
        fill="currentColor"
        fillOpacity={0.05}
      />
      {/* czoło tuby (akcent) */}
      <ellipse cx={90} cy={78} rx={4} ry={22} fill={ACCENT} fillOpacity={0.14} stroke={ACCENT} />
      {/* ustnik */}
      <path {...stroke} d="M62 72 L56 74 L56 82 L62 84" />
      {/* uchwyt */}
      <path {...stroke} d="M74 90 v9 a4 4 0 0 0 8 0 v-5" />
    </g>

    {/* fale dźwięku */}
    <g {...stroke} opacity={0.6}>
      <path className="wave" d="M100 64 q10 14 0 28" />
      <path className="wave wave-2" d="M110 58 q15 20 0 40" />
    </g>

    <Sparks />
  </AlertsSvg>
);

// Zgłoszenia gości — podkładka z notatką i pieczątką „ptaszka" (zrealizowane).
export const ReportsIllustration: FC = () => (
  <ReportsSvg viewBox="0 0 220 150" role="img" aria-hidden="true">
    <Blobs />
    <ellipse cx={108} cy={126} rx={44} ry={6} fill="currentColor" opacity={0.08} />

    <g className="floaty">
      {/* podkładka (clipboard) */}
      <rect {...stroke} x={78} y={58} width={60} height={66} rx={6} fill="currentColor" fillOpacity={0.04} />
      {/* klips u góry */}
      <rect {...stroke} x={96} y={52} width={24} height={12} rx={3} fill="currentColor" fillOpacity={0.08} />
      <path {...stroke} d="M104 58 h8" />
      {/* wiersze treści */}
      <g stroke="currentColor" strokeWidth={3} strokeLinecap="round" opacity={0.35}>
        <path d="M90 80 h30" />
        <path d="M90 92 h36" />
        <path d="M90 104 h22" />
      </g>
    </g>

    {/* pieczątka „ptaszek" (pulsuje) */}
    <g className="pop">
      <circle cx={128} cy={106} r={12} fill={ACCENT} fillOpacity={0.14} stroke={ACCENT} strokeWidth={2} />
      <path
        d="M122 106 l4 4 8 -9"
        fill="none"
        stroke={ACCENT}
        strokeWidth={2.4}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>

    <Sparks />
  </ReportsSvg>
);

// Punkty POI / atrakcje w pobliżu — pinezka mapy z pulsującym „namierzeniem".
export const PoiIllustration: FC = () => (
  <PoiSvg viewBox="0 0 220 150" role="img" aria-hidden="true">
    <Blobs />

    {/* pulsujący pierścień „lokalizacji" pod pinezką */}
    <ellipse className="ping" cx={110} cy={120} rx={20} ry={6} fill="none" stroke={ACCENT} strokeWidth={2} />
    <ellipse cx={110} cy={120} rx={30} ry={7} fill="currentColor" opacity={0.07} />

    {/* pinezka */}
    <g className="floaty">
      <path
        {...stroke}
        d="M110 56 C97 56 87 66 87 79 C87 96 110 118 110 118 C110 118 133 96 133 79 C133 66 123 56 110 56 Z"
        fill={ACCENT}
        fillOpacity={0.14}
        stroke={ACCENT}
      />
      <circle cx={110} cy={79} r={8} fill="none" stroke={ACCENT} strokeWidth={2} />
      <circle cx={110} cy={79} r={3} fill={ACCENT} stroke="none" />
    </g>

    <Sparks />
  </PoiSvg>
);

// Galeria zdjęć — dwie nachodzące na siebie ramki ze zdjęciami (słońce + góry).
export const GalleryIllustration: FC = () => (
  <GallerySvg viewBox="0 0 220 150" role="img" aria-hidden="true">
    <Blobs />
    <ellipse cx={110} cy={124} rx={50} ry={6} fill="currentColor" opacity={0.08} />

    <g className="floaty">
      {/* tylna ramka (przekrzywiona) */}
      <g transform="rotate(-9 96 74)">
        <rect {...stroke} x={66} y={52} width={58} height={46} rx={5} fill="currentColor" fillOpacity={0.04} opacity={0.7} />
        <path {...stroke} d="M72 90 l11 -11 l8 8" opacity={0.5} />
        <circle cx={82} cy={66} r={4} fill="currentColor" opacity={0.4} />
      </g>

      {/* przednia ramka */}
      <rect {...stroke} x={92} y={58} width={64} height={52} rx={5} fill="currentColor" fillOpacity={0.05} />
      <path {...stroke} d="M98 102 l15 -15 l10 10 M118 102 l13 -13 l19 19" opacity={0.6} />
      <circle className="sun" cx={112} cy={76} r={5.5} fill={ACCENT} fillOpacity={0.25} stroke={ACCENT} strokeWidth={2} />
    </g>

    <Sparks />
  </GallerySvg>
);

// Upload plików — kartka dokumentu w tle i miniatura obrazka (słońce + góry)
// na wierzchu, z unoszącym się badge'em strzałki w górę. Motyw „grafiki i dokumenty".
export const UploadIllustration: FC = () => (
  <UploadSvg viewBox="0 0 220 150" role="img" aria-hidden="true">
    <Blobs />
    <ellipse cx={110} cy={122} rx={52} ry={6} fill="currentColor" opacity={0.08} />

    <g className="floaty">
      {/* dokument w tle (lekko przekrzywiony) */}
      <g transform="rotate(-8 90 80)">
        <path
          {...stroke}
          d="M66 46 h38 l12 12 v50 a4 4 0 0 1 -4 4 H66 a4 4 0 0 1 -4 -4 V50 a4 4 0 0 1 4 -4 Z"
          fill="currentColor"
          fillOpacity={0.05}
        />
        {/* zagięty róg */}
        <path {...stroke} d="M104 46 v12 h12" fill="none" opacity={0.7} />
        {/* linie tekstu */}
        <path {...stroke} d="M72 74 h32" opacity={0.4} />
        <path {...stroke} d="M72 84 h32" opacity={0.4} />
        <path {...stroke} d="M72 94 h20" opacity={0.4} />
      </g>

      {/* miniatura obrazka na wierzchu */}
      <rect {...stroke} x={104} y={62} width={62} height={50} rx={5} fill="currentColor" fillOpacity={0.05} />
      <path {...stroke} d="M110 104 l13 -13 l9 9 M127 104 l13 -13 l19 19" opacity={0.6} />
      <circle className="sun" cx={124} cy={80} r={5.5} fill={ACCENT} fillOpacity={0.25} stroke={ACCENT} strokeWidth={2} />
    </g>

    {/* badge uploadu — strzałka w górę (akcent) */}
    <g className="arrow">
      <circle cx={150} cy={48} r={11} fill={ACCENT} stroke={ACCENT} strokeWidth={2} />
      <path
        d="M150 54 v-11 M144 49 l6 -6 l6 6"
        fill="none"
        stroke="#ffffff"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>

    <Sparks />
  </UploadSvg>
);

// Osoba / dyrektor — popiersie avatara z akcentowanym „plusem" (dodaj osobę).
export const PersonIllustration: FC = () => (
  <PersonSvg viewBox="0 0 220 150" role="img" aria-hidden="true">
    <Blobs />
    <ellipse cx={110} cy={126} rx={42} ry={6} fill="currentColor" opacity={0.08} />

    <g className="floaty">
      {/* popiersie */}
      <path
        {...stroke}
        d="M82 118 a28 24 0 0 1 56 0 Z"
        fill="currentColor"
        fillOpacity={0.05}
      />
      {/* głowa */}
      <circle {...stroke} cx={110} cy={66} r={16} fill="currentColor" fillOpacity={0.05} />
    </g>

    {/* badge „+" (akcent) */}
    <g className="pop">
      <circle cx={132} cy={92} r={9} fill={ACCENT} stroke={ACCENT} strokeWidth={2} />
      <path d="M132 87.5 v9 M127.5 92 h9" stroke="#ffffff" strokeWidth={2} strokeLinecap="round" />
    </g>

    <Sparks />
  </PersonSvg>
);

// Konfiguracja / aplikacja mobilna wyłączona — telefon z przełącznikiem OFF
// i obracającym się trybikiem ustawień (motyw „włącz w ustawieniach").
export const SetupIllustration: FC = () => (
  <SetupSvg viewBox="0 0 220 150" role="img" aria-hidden="true">
    <Blobs />
    <ellipse cx={107} cy={126} rx={40} ry={6} fill="currentColor" opacity={0.08} />

    <g className="floaty">
      {/* korpus telefonu */}
      <rect {...stroke} x={84} y={34} width={46} height={82} rx={10} fill="currentColor" fillOpacity={0.05} />
      {/* głośnik / notch */}
      <path {...stroke} d="M101 40 h12" opacity={0.6} />
      {/* pasek „home" */}
      <path {...stroke} d="M100 110 h14" opacity={0.5} />
      {/* etykieta aplikacji na ekranie */}
      <path {...stroke} d="M96 56 h22" opacity={0.3} />

      {/* przełącznik OFF (aplikacja wyłączona) — gałka po lewej, stonowana */}
      <rect
        x={96}
        y={66}
        width={22}
        height={11}
        rx={5.5}
        fill="currentColor"
        fillOpacity={0.12}
        stroke="currentColor"
        strokeWidth={2}
      />
      <circle cx={101.5} cy={71.5} r={3.4} fill="currentColor" />

      {/* trybik ustawień (obraca się) — niewidzialny kwadrat centruje obrót */}
      <g className="gear">
        <rect x={116} y={90} width={28} height={28} fill="none" stroke="none" />
        <circle cx={130} cy={104} r={8.5} fill={ACCENT} fillOpacity={0.14} stroke={ACCENT} strokeWidth={2} />
        <g stroke={ACCENT} strokeWidth={2.4} strokeLinecap="round">
          <path d="M130 92 v-4" />
          <path d="M130 116 v4" />
          <path d="M118 104 h-4" />
          <path d="M142 104 h4" />
          <path d="M121.4 95.4 l-2.8 -2.8" />
          <path d="M138.6 112.6 l2.8 2.8" />
          <path d="M138.6 95.4 l2.8 -2.8" />
          <path d="M121.4 112.6 l-2.8 2.8" />
        </g>
        <circle cx={130} cy={104} r={3} fill={ACCENT} />
      </g>
    </g>

    <Sparks />
  </SetupSvg>
);

// Grupy użytkowników — trzy nachodzące popiersia avatarów (segment gości).
export const GroupIllustration: FC = () => (
  <GroupSvg viewBox="0 0 220 150" role="img" aria-hidden="true">
    <Blobs />
    <ellipse cx={110} cy={126} rx={50} ry={6} fill="currentColor" opacity={0.08} />

    <g className="floaty">
      {/* lewy avatar (w tle) */}
      <g {...stroke} opacity={0.5}>
        <circle cx={82} cy={72} r={12} fill="currentColor" fillOpacity={0.05} />
        <path d="M64 106 a18 16 0 0 1 36 0 Z" fill="currentColor" fillOpacity={0.05} />
      </g>
      {/* prawy avatar (w tle) */}
      <g {...stroke} opacity={0.5}>
        <circle cx={138} cy={72} r={12} fill="currentColor" fillOpacity={0.05} />
        <path d="M120 106 a18 16 0 0 1 36 0 Z" fill="currentColor" fillOpacity={0.05} />
      </g>
      {/* środkowy avatar (przód, akcent, pulsuje) */}
      <g className="lead" {...stroke}>
        <path d="M88 110 a22 19 0 0 1 44 0 Z" fill={ACCENT} fillOpacity={0.12} stroke={ACCENT} />
        <circle cx={110} cy={64} r={15} fill={ACCENT} fillOpacity={0.16} stroke={ACCENT} />
      </g>
    </g>

    <Sparks />
  </GroupSvg>
);

// Organizacje — klaster budynków z akcentowanym HQ (maszt + flaga). Unikalne dla
// pustego stanu / błędu ładowania listy organizacji.
export const OrganizationsIllustration: FC = () => (
  <OrganizationsSvg viewBox="0 0 220 150" role="img" aria-hidden="true">
    <Blobs />
    <ellipse cx={110} cy={126} rx={58} ry={6} fill="currentColor" opacity={0.08} />

    <g className="floaty">
      {/* budynek w tle (lewy) */}
      <g {...stroke} opacity={0.45}>
        <rect x={52} y={64} width={40} height={60} rx={3} fill="currentColor" fillOpacity={0.05} />
        <path d="M60 76 h9 M75 76 h9 M60 90 h9 M75 90 h9 M60 104 h9 M75 104 h9" />
      </g>
      {/* budynek w tle (prawy) */}
      <g {...stroke} opacity={0.45}>
        <rect x={130} y={72} width={38} height={52} rx={3} fill="currentColor" fillOpacity={0.05} />
        <path d="M138 84 h9 M153 84 h9 M138 98 h9 M153 98 h9" />
      </g>
      {/* budynek główny HQ (przód, akcent, „oddycha") */}
      <g className="lead" {...stroke}>
        <rect x={90} y={50} width={40} height={74} rx={4} fill={ACCENT} fillOpacity={0.12} stroke={ACCENT} />
        {/* maszt + flaga */}
        <path d="M110 50 v-12" stroke={ACCENT} />
        <path d="M110 38 h13 v8 h-13 z" fill={ACCENT} fillOpacity={0.7} stroke={ACCENT} />
        {/* okna */}
        <path
          d="M98 64 h10 M112 64 h10 M98 78 h10 M112 78 h10 M98 92 h10 M112 92 h10"
          stroke={ACCENT}
          opacity={0.85}
        />
        {/* drzwi */}
        <path d="M104 124 v-13 a6 6 0 0 1 12 0 v13" stroke={ACCENT} />
      </g>
    </g>

    <Sparks />
  </OrganizationsSvg>
);

// Spa / wellness — kwiat lotosu nad taflą wody (pulsujące płatki, fale).
export const SpaIllustration: FC = () => {
  const petals = [-54, -27, 0, 27, 54];
  return (
    <SpaSvg viewBox="0 0 220 150" role="img" aria-hidden="true">
      <Blobs />

      {/* tafla wody — rozchodzące się fale */}
      <ellipse className="ripple" cx={110} cy={116} rx={26} ry={6} fill="none" stroke={ACCENT} strokeWidth={2} opacity={0.5} />
      <ellipse cx={110} cy={116} rx={40} ry={8} fill="currentColor" opacity={0.07} />

      <g className="floaty">
        {/* płatki boczne */}
        <g {...stroke}>
          {petals.map((angle) => (
            <path
              key={angle}
              transform={`rotate(${angle} 110 112)`}
              d="M110 112 C103 92 103 70 110 56 C117 70 117 92 110 112 Z"
              fill={ACCENT}
              fillOpacity={angle === 0 ? 0.18 : 0.07}
              stroke={ACCENT}
              opacity={angle === 0 ? 1 : 0.6}
              className={angle === 0 ? 'bloom' : undefined}
            />
          ))}
        </g>
      </g>

      <Sparks />
    </SpaSvg>
  );
};

// AI Agent — przyjazny robot-asystent: mruga oczami, pulsuje dioda na antenie,
// obok dymek z „piszącymi" kropkami (rozmowa). Akcent teal na detalach.
export const AiIllustration: FC = () => (
  <AiSvg viewBox="0 0 220 150" role="img" aria-hidden="true">
    <Blobs />

    {/* cień / rozchodzący się pierścień pod robotem */}
    <ellipse className="ripple" cx={96} cy={118} rx={24} ry={5} fill="none" stroke={ACCENT} strokeWidth={2} opacity={0.5} />
    <ellipse cx={96} cy={118} rx={38} ry={7} fill="currentColor" opacity={0.06} />

    <g className="floaty">
      {/* antena z pulsującą diodą */}
      <path d="M96 52 V42" {...stroke} />
      <circle className="beacon" cx={96} cy={38} r={4} fill={ACCENT} stroke={ACCENT} />

      {/* głowa */}
      <rect x={68} y={52} width={56} height={50} rx={14} {...stroke} fill={ACCENT} fillOpacity={0.06} />
      {/* uszy / boczne moduły */}
      <rect x={60} y={69} width={6} height={14} rx={3} {...stroke} />
      <rect x={126} y={69} width={6} height={14} rx={3} {...stroke} />

      {/* oczy (mrugają) */}
      <g className="blink">
        <circle cx={85} cy={75} r={4.6} fill={ACCENT} />
        <circle cx={107} cy={75} r={4.6} fill={ACCENT} />
      </g>
      {/* uśmiech */}
      <path d="M84 87 Q96 96 108 87" {...stroke} />

      {/* dymek rozmowy z piszącymi kropkami */}
      <rect x={140} y={62} width={50} height={30} rx={8} {...stroke} fill="currentColor" fillOpacity={0.04} />
      <path d="M150 92 l0 8 l9 -8 Z" {...stroke} fill="currentColor" fillOpacity={0.04} />
      <circle className="dot dot-1" cx={154} cy={77} r={2.6} fill={ACCENT} />
      <circle className="dot dot-2" cx={165} cy={77} r={2.6} fill={ACCENT} />
      <circle className="dot dot-3" cx={176} cy={77} r={2.6} fill={ACCENT} />
    </g>

    <Sparks />
  </AiSvg>
);

// Stan błędu — dwa rozłączone wtyki kabla z migoczącą iskrą w przerwie
// („utracone połączenie / coś poszło nie tak"). Akcent teal na iskrze.
export const ErrorIllustration: FC = () => (
  <ErrorSvg viewBox="0 0 220 150" role="img" aria-hidden="true">
    <Blobs />

    {/* kable schodzące do dołu */}
    <path d="M54 80 C40 92 34 106 32 124" {...stroke} />
    <path d="M166 80 C180 92 186 106 188 124" {...stroke} />

    <g className="floaty">
      {/* lewy wtyk + bolce */}
      <rect x={52} y={62} width={26} height={30} rx={7} {...stroke} fill="currentColor" fillOpacity={0.05} />
      <path d="M78 72 h13 M78 82 h13" {...stroke} />

      {/* prawy wtyk + bolce */}
      <rect x={142} y={62} width={26} height={30} rx={7} {...stroke} fill="currentColor" fillOpacity={0.05} />
      <path d="M129 72 h13 M129 82 h13" {...stroke} />

      {/* iskra w przerwie */}
      <path
        className="zap"
        d="M115 60 l-9 17 l7 0 l-7 17"
        fill="none"
        stroke={ACCENT}
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>

    <Sparks />
  </ErrorSvg>
);

// 404 — kompas z kołyszącą się igłą („nie można odnaleźć kierunku/strony").
// Północna połowa igły w akcentowym teal.
export const NotFoundIllustration: FC = () => (
  <NotFoundSvg viewBox="0 0 220 150" role="img" aria-hidden="true">
    <Blobs />

    {/* cień pod kompasem */}
    <ellipse cx={110} cy={128} rx={34} ry={6} fill="currentColor" opacity={0.06} />

    <g className="floaty">
      {/* obudowa kompasu */}
      <circle cx={110} cy={82} r={36} {...stroke} fill="currentColor" fillOpacity={0.04} />
      <circle cx={110} cy={82} r={29} {...stroke} opacity={0.45} />
      {/* znaczniki N / E / S / W */}
      <path d="M110 49 v7 M110 108 v7 M77 82 h7 M136 82 h7" {...stroke} />

      {/* igła kompasu */}
      <g className="needle">
        <path d="M110 60 L117 82 L103 82 Z" {...stroke} fill={ACCENT} stroke={ACCENT} />
        <path d="M110 104 L117 82 L103 82 Z" {...stroke} fill="currentColor" fillOpacity={0.15} />
      </g>
      <circle cx={110} cy={82} r={3.5} fill={ACCENT} />
    </g>

    <Sparks />
  </NotFoundSvg>
);

// Domyślna ilustracja — taca/skrzynka odbiorcza z unoszącą się kartką.
export const GenericIllustration: FC = () => (
  <GenericSvg viewBox="0 0 220 150" role="img" aria-hidden="true">
    <Blobs />
    <ellipse cx={110} cy={125} rx={46} ry={6} fill="currentColor" opacity={0.08} />

    {/* unosząca się kartka */}
    <g className="floaty" {...stroke}>
      <rect x={92} y={50} width={36} height={44} rx={4} fill="currentColor" fillOpacity={0.05} />
      <path d="M100 62 h20 M100 70 h20 M100 78 h12" opacity={0.5} />
      <circle cx={132} cy={56} r={4} fill={ACCENT} stroke={ACCENT} opacity={0.75} />
    </g>

    {/* skrzynka odbiorcza */}
    <g {...stroke}>
      <path
        d="M72 100 h22 l5 9 h22 l5 -9 h22 v16 a6 6 0 0 1 -6 6 H78 a6 6 0 0 1 -6 -6 z"
        fill="currentColor"
        fillOpacity={0.05}
      />
      <path d="M72 100 l8 -16 h60 l8 16" />
    </g>

    <Sparks />
  </GenericSvg>
);

// Generator kodów QR — unoszący się kod QR (rogi pozycjonujące + moduły) z linią
// skanującą przesuwającą się w pionie i pulsującym akcentowym modułem.
export const QrIllustration: FC = () => (
  <QrSvg viewBox="0 0 220 150" role="img" aria-hidden="true">
    <Blobs />
    <ellipse cx={110} cy={128} rx={44} ry={5} fill="currentColor" opacity={0.08} />

    <g className="floaty">
      {/* ramka kodu QR */}
      <g {...stroke}>
        <rect x={80} y={42} width={60} height={60} rx={8} fill="currentColor" fillOpacity={0.04} />
      </g>

      {/* trzy rogi pozycjonujące (finder patterns) */}
      <g {...stroke}>
        <rect x={87} y={49} width={15} height={15} rx={3} />
        <rect className="qr-pulse" x={118} y={49} width={15} height={15} rx={3} stroke={ACCENT} />
        <rect x={87} y={80} width={15} height={15} rx={3} />
      </g>
      <g fill="currentColor">
        <rect x={91} y={53} width={7} height={7} rx={1.5} />
        <rect x={91} y={84} width={7} height={7} rx={1.5} />
      </g>
      <rect x={122} y={53} width={7} height={7} rx={1.5} fill={ACCENT} />

      {/* moduły danych (dolny-prawy kwadrant) */}
      <g fill="currentColor" opacity={0.55}>
        <rect x={112} y={78} width={5} height={5} rx={1} />
        <rect x={121} y={78} width={5} height={5} rx={1} />
        <rect x={130} y={78} width={5} height={5} rx={1} />
        <rect x={112} y={87} width={5} height={5} rx={1} />
        <rect x={121} y={87} width={5} height={5} rx={1} />
        <rect x={116} y={96} width={5} height={5} rx={1} />
        <rect x={130} y={87} width={5} height={5} rx={1} />
      </g>

      {/* linia skanująca */}
      <line className="qr-scan" x1={84} y1={72} x2={136} y2={72} stroke={ACCENT} strokeWidth={2} strokeLinecap="round" opacity={0.85} />
    </g>

    <Sparks />
  </QrSvg>
);
